
import { Companies, Quotes } from "@/api";
import TitleHeader from "@/components/common/TitleHeader.vue";
import QuoteFormModal from "@/components/companies/QuoteFormModal.vue";
import moment from "moment";
import { nextTick } from "process";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";

const namespace = "auth";

@Component({
  components: {
    ValidationProvider,
    ValidationObserver,
    TitleHeader,
    QuoteFormModal
  }
})
export default class QuotesListPage extends Vue {
  filters: any = {
    name: "",
    dateFrom: undefined,
    dateTo: undefined,
    statusId: undefined,
    companyName: undefined,
    ccAgent: undefined
  };
  timeout = 0;
  quotes = [];
  page = 1;
  options = {};
  itemsPerPage = 10;
  total = 0;
  menu = false;
  menuSecond = false;
  statuses = [
    {
      id: 1,
      name: "Pending"
    },
    {
      id: 2,
      name: "Accepted"
    },
    {
      id: 3,
      name: "Declined"
    }
  ];
  headers = [
    {
      text: "Quote ID",
      value: "id",
      width: "100px"
    },
    {
      text: "Quote Date",
      value: "desiredDate",
      width: "150px"
    },
    {
      text: "Company Name",
      value: "companyName",
      width: "250px"
    },
    {
      text: "Client Name",
      value: "clientName",
      width: "150px"
    },
    {
      text: "CC Agent Name",
      value: "ccAgent",
      width: "200px"
    },
    {
      text: "Course Name",
      value: "courses"
    },
    {
      text: "Total Price",
      value: "totalPrice",
      width: "130px"
    },
    {
      text: "Status",
      value: "statusName",
      width: "120px"
    },
    {
      text: "Manage",
      value: "manage",
      width: "200px"
    },
    {
      text: "Actions",
      value: "actions",
      width: "200px"
    }
  ];
  items = [];
  companies = [];
  ccAgents = [];
  selectedQuote: any = null;
  quoteRequestSequence = 0;

  /** LMS-1224: the create-quote popup lives here now. */
  creating = false;

  @Getter("getUser", { namespace })
  getUser!: object | null;

  get canCreate(): boolean {
    return this.hasPermission("create_quote");
  }

  get canManage(): boolean {
    return this.hasPermission("manage_quote");
  }

  hasPermission(name: string): boolean {
    const currentUser: any = this.getUser;
    return currentUser && currentUser.permissions
      ? Boolean(
          currentUser.permissions.find(
            (permission: string) => permission === name
          )
        )
      : false;
  }

  onCreate() {
    this.creating = true;
    /** The modal is mounted by v-if, so it can only be opened on the next tick. */
    this.$nextTick(() => this.$modal.show("quote-form-modal"));
  }

  async onCreated() {
    this.creating = false;
    await this.loadQuotesData(this.page, this.filters);
    this.$success("<strong>Success!</strong>Quote has been created!");
  }

  @Watch("filters", { deep: true })
  async onFilterNameChange() {
    await this.loadQuotesData(1, this.filters);
  }
  @Watch("page", { deep: true })
  async onPageNameChange(val: any) {
    await this.loadQuotesData(val, this.filters);
  }
  async loadQuotesData(page = 1, filters: any) {
    const requestSequence = ++this.quoteRequestSequence;

    try {
      const response: any = await Quotes.loadQuotesData(page, filters);

      // Fast filter changes can complete out of order. Only the latest query
      // is allowed to replace the rows currently displayed in the table.
      if (requestSequence !== this.quoteRequestSequence) {
        return;
      }

      this.items = response.data;
      this.itemsPerPage = response.meta.perPage;
      this.page = response.meta.currentPage;
      this.total = response.meta.total;
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors);
    }
  }
  async created() {
    try {
      await this.loadQuotesData(1, this.filters);
      this.companies = await Companies.listWithoutPagination();
      this.ccAgents = await Companies.loadCcAgents();
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors);
    }
  }
  async acceptQuote(id: number) {
    try {
      await Quotes.accept(id);
      await this.loadQuotesData(1, this.filters);
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors);
    }
  }
  async declineQuote(id: number) {
    try {
      await Quotes.decline(id);
      await this.loadQuotesData(1, this.filters);
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors);
    }
  }
  async getSingleQuote(id: number) {
    try {
      const data: any = await Quotes.getSingleQuote(id);
      const file = new Blob([data.data], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, "_blank");
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors);
    }
  }
  @Watch("options", { deep: true })
  async onOptionsChange(val: any) {
    await this.loadQuotesData(val.page, this.filters);
  }

  formatDate(date: string) {
    return moment(date).format("DD/MM/YYYY");
  }
  async onExport() {
    try {
      const fileName = `quotes-${moment().format("DD/MM/YYYY")}.xlsx`;
      const data = await Quotes.exportQuotes(this.filters);
      const downloadUrl = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      const err = e as any;
      await this.$error(err.errors);
    }
  }
  editQuote(quote: any) {
    this.selectedQuote = quote;

    nextTick(() => {
      this.$modal.show("quote-form-modal");
    });
  }
  onUpdated() {
    this.loadQuotesData(this.page, this.filters);
    this.$success("<strong>Success!</strong>Quote has been updated!");
  }
  formatCoursesNames(courses: any[]) {
    const coursesList = courses.map(course => course.name);
    const uniqueCoursesList = Array.from(new Set(coursesList));

    return uniqueCoursesList.join(",<br />");
  }
}
